

import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ErrorMessageHandlerMixin from '../misc/ErrorMessageHandler.mixin';
import { mixins } from 'vue-class-component';
import { required, email, minLength, numeric } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Company from '@/models/Company';
import { namespace } from 'vuex-class';
import { companyStoreActions } from '@/store/company.store';
import { Country } from '@/misc/allCountries';
import { applicationStoreMutations } from '@/store/application.store';

const CompanyStore = namespace('company');
const ApplicationStore = namespace('application');

@Component({
    mixins: [validationMixin],
    validations: {
        phoneNumber: { required, minLength: minLength(5), numeric },
        companyCopy: {
            name: {
                required,
                minLength: minLength(3)
            },
            street: {
                required,
                minLength: minLength(3)
            },
            postBox: {
                required,
                minLength: minLength(1)
            },
            city: {
                required,
                minLength: minLength(3)
            },
            email: {
                required,
                email
            }
        }
    },
    components: {
        CountyCodeComponent: () => import(
            /* webpackChunkName: "CountyCodeComponent" */
            '@/components/CountyCode.component.vue')
    }
})
export default class EditCompanyComponent extends mixins(ErrorMessageHandlerMixin) {

    @Prop({ default: () => new Company() })
    public company!: Company;

    @CompanyStore.Action(companyStoreActions.CREATE)
    public createCompanyAction!: (payload: { company: Company }) => Promise<Company>;

    @CompanyStore.Action(companyStoreActions.UPDATE)
    public updateCompanyAction!: (payload: { company: Company }) => Promise<Company>;

    @ApplicationStore.Mutation(applicationStoreMutations.SET_COMPANY)
    public setCompany!: (company: Company) => void;

    public companyCopy: Company = new Company();
    public validData = false;
    public isLoading: boolean = false;
    private prefix: string = '+264';
    private phoneNumber: string = '';

    @Watch('company', { immediate: true })
    public companyChanged() {
        if (this.company) {
            this.companyCopy = this.company.copy() as Company;
            if (this.companyCopy.phone?.prefix) this.prefix = this.companyCopy.phone.prefix;
            if (this.companyCopy.phone?.phoneNumber) this.phoneNumber = this.companyCopy.phone.phoneNumber;
            this.validData = !this.$v!.$invalid;
        }
    }

    private onSelect(payload: Country): void {
        if (!payload) return;
        this.prefix = `+${payload.dialCode}`;
    }

    /**
     * User status to select from
     */
    private selectStatus: { text: string, value: boolean } [] = [
        { text: this.$t('COMPANY.DIALOG.STATUS_ACTIVE').toString(), value: true },
        { text: this.$t('COMPANY.DIALOG.STATUS_INACTIVE').toString(), value: false }
    ];

    public async saveData() {
        const companyToUse = Company.parseFromObject({
            ...this.companyCopy,
            phone: {
                prefix: this.prefix,
                phoneNumber: this.phoneNumber
            }
        });

        if (this.validData) {
            try {
                this.isLoading = true;
                if (!this.companyCopy.id) {
                    const createdCompany = await this.createCompanyAction({ company: companyToUse });
                    this.setCompany(createdCompany);
                    this.$notifySuccessSimplified('COMPANY.CREATED_SUCCESS');
                } else {
                    await this.updateCompanyAction({ company: companyToUse });
                    this.$notifySuccessSimplified('COMPANY.EDITED_SUCCESS');
                }
                this.dismiss(true);
            } catch (err) {
                if (err.status === 409) { // Duplicate
                    Vue.notifyErrorSimplified('GENERAL.NOTIFICATIONS.DUPLICATE_COMPANY_ERROR');
                } else {
                    Vue.notifyErrorSimplified('GENERAL.NOTIFICATIONS.SERVER_ERROR');
                }
            } finally {
                this.isLoading = false;
            }
        }
    }

    public dismiss(reloadData: boolean = false) {
        if (reloadData) {
            this.companyCopy = new Company();
        }

        this.$v.$reset();
        this.$emit('closeDialog', reloadData);
    }

    public checkForm(type: string) {
        this.validData = !this.$v!.$invalid;
        this.triggerValidation(type);
    }
}

